import React, { useState } from "react";

import { formatINR } from "../../../utils/format";
import { CurrencyInput } from "../../../components/CurrencyInput";
import { MobileSelect } from "../../../components/ui/MobileSelect";
import type { PaymentMode, TicketEntryLog } from "../../../types";
import {
  computeEntryLogPagination,
  ENTRY_LOG_INITIAL_VISIBLE,
  formatEntryLogRange,
  showMoreLabel,
  type EntryLogPagination,
} from "./ticketEntryLogPagination";

function formatWhen(iso: string): string {
  try {
    return new Date(iso).toLocaleString("en-IN", { dateStyle: "short", timeStyle: "short" });
  } catch {
    return iso;
  }
}

type Props = {
  entries: TicketEntryLog[];
  visibleCount: number;
  totalCount: number;
  olderCursor: string | null;
  loadingMore: boolean;
  onShowMore: () => void;
  onShowAllLoaded: () => void;
  onCollapse: () => void;
  onExportCsv: () => void;
  exportingCsv: boolean;
  operatorLabel: (entry: TicketEntryLog) => string;
  onUpdateEntry?: (entryId: string, payload: { entry_cost?: number; payment_mode?: PaymentMode }) => void;
  updatingEntryId?: string | null;
};

const PAYMENT_MODE_LABELS: Record<PaymentMode, string> = { cash: "Cash", online: "Online" };

export const TicketEntryLogList: React.FC<Props> = ({
  entries,
  visibleCount,
  totalCount,
  olderCursor,
  loadingMore,
  onShowMore,
  onShowAllLoaded,
  onCollapse,
  onExportCsv,
  exportingCsv,
  operatorLabel,
  onUpdateEntry,
  updatingEntryId,
}) => {
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editAmount, setEditAmount] = useState("");
  const [editPaymentMode, setEditPaymentMode] = useState<PaymentMode | "">("");

  const startEdit = (entry: TicketEntryLog) => {
    setEditingId(entry.id);
    setEditAmount(String(entry.entry_cost));
    setEditPaymentMode(entry.payment_mode ?? "");
  };

  const cancelEdit = () => setEditingId(null);

  const saveEdit = (entryId: string) => {
    if (!onUpdateEntry) return;
    const amount = Number(editAmount);
    onUpdateEntry(entryId, {
      entry_cost: Number.isFinite(amount) ? amount : undefined,
      payment_mode: editPaymentMode || undefined,
    });
    setEditingId(null);
  };
  const pagination: EntryLogPagination = computeEntryLogPagination(
    visibleCount,
    entries.length,
    totalCount,
    olderCursor,
  );
  const visibleEntries = entries.slice(0, pagination.shownCount);

  return (
    <section className="entry-log-list" aria-label="Tier entry history">
      <div className="entry-log-list__status">
        <div className="entry-log-list__status-main">
          <div className="entry-log-list__status-text">
            <span className="entry-log-list__status-strong">{formatEntryLogRange(pagination)}</span>
            {pagination.hasMoreOnServer ? (
              <span className="entry-log-list__status-muted"> · more on server</span>
            ) : null}
          </div>
          <button
            type="button"
            className="eventos-nav-btn entry-log-list__export-btn"
            disabled={exportingCsv || pagination.totalCount === 0}
            aria-busy={exportingCsv}
            onClick={onExportCsv}
          >
            {exportingCsv ? "Exporting…" : "Export all to CSV"}
          </button>
        </div>
        <div
          className="entry-log-list__progress"
          role="progressbar"
          aria-valuenow={pagination.progressPercent}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label={`${pagination.progressPercent}% of ${pagination.totalCount} entries visible`}
        >
          <div
            className="entry-log-list__progress-fill"
            style={{ width: `${pagination.progressPercent}%` }}
          />
        </div>
      </div>

      <div className="eventos-table-wrap entry-log-list__table">
        <table className="eventos-table">
          <thead>
            <tr>
              <th>When</th>
              <th>Tier</th>
              <th className="eventos-table__num">Qty</th>
              <th>Operator</th>
              <th className="eventos-table__num">Paid</th>
              <th>Mode</th>
              <th>Notes</th>
              {onUpdateEntry ? <th /> : null}
            </tr>
          </thead>
          <tbody>
            {visibleEntries.map((entry) => {
              const isEditing = editingId === entry.id;
              return (
                <tr key={entry.id}>
                  <td>{formatWhen(entry.occurred_at)}</td>
                  <td>{entry.tier_name ?? "—"}</td>
                  <td className="eventos-table__num">{entry.quantity_sold ?? 1}</td>
                  <td>{operatorLabel(entry)}</td>
                  <td className="eventos-table__num">
                    {isEditing ? (
                      <CurrencyInput value={editAmount} onChange={(e) => setEditAmount(e.target.value)} step="any" />
                    ) : (
                      formatINR(entry.entry_cost)
                    )}
                  </td>
                  <td>
                    {isEditing ? (
                      <MobileSelect
                        label="Payment mode"
                        className="eventos-select"
                        value={editPaymentMode}
                        onChange={(v) => setEditPaymentMode(v as PaymentMode | "")}
                        options={[
                          { value: "", label: "—" },
                          { value: "cash", label: "Cash" },
                          { value: "online", label: "Online" },
                        ]}
                      />
                    ) : (
                      (entry.payment_mode && PAYMENT_MODE_LABELS[entry.payment_mode]) || "—"
                    )}
                  </td>
                  <td>{entry.notes?.trim() || "—"}</td>
                  {onUpdateEntry ? (
                    <td>
                      {isEditing ? (
                        <div style={{ display: "flex", gap: 4 }}>
                          <button
                            type="button"
                            className="eventos-link-btn"
                            disabled={updatingEntryId === entry.id}
                            onClick={() => saveEdit(entry.id)}
                          >
                            {updatingEntryId === entry.id ? "Saving…" : "Save"}
                          </button>
                          <button type="button" className="eventos-link-btn" onClick={cancelEdit}>
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <button type="button" className="eventos-link-btn" onClick={() => startEdit(entry)}>
                          Edit
                        </button>
                      )}
                    </td>
                  ) : null}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {pagination.canShowMore || pagination.canCollapse ? (
        <div className="entry-log-list__actions">
          {pagination.canShowMore ? (
            <button
              type="button"
              className="eventos-primary-btn entry-log-list__btn-primary"
              disabled={loadingMore}
              aria-busy={loadingMore}
              onClick={onShowMore}
            >
              {loadingMore ? "Loading entries…" : showMoreLabel(pagination)}
            </button>
          ) : null}
          {pagination.canShowAllLoaded ? (
            <button
              type="button"
              className="eventos-nav-btn"
              disabled={loadingMore}
              onClick={onShowAllLoaded}
            >
              Show all loaded ({pagination.loadedCount})
            </button>
          ) : null}
          {pagination.canCollapse ? (
            <button
              type="button"
              className="eventos-nav-btn entry-log-list__btn-ghost"
              disabled={loadingMore}
              onClick={onCollapse}
            >
              Show recent only ({ENTRY_LOG_INITIAL_VISIBLE})
            </button>
          ) : null}
        </div>
      ) : null}

      {pagination.canShowMore ? (
        <p className="entry-log-list__hint eventos-subtle" aria-live="polite">
          {pagination.hiddenLoadedCount > 0
            ? `${pagination.hiddenLoadedCount} more loaded ${pagination.hiddenLoadedCount === 1 ? "entry is" : "entries are"} hidden.`
            : `${pagination.remainingTotalCount} older ${pagination.remainingTotalCount === 1 ? "entry" : "entries"} on the server — use Show more to load them.`}
        </p>
      ) : null}
    </section>
  );
};
